import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Leaf,
  HandHeart,
  HardHat,
  Scale,
  Map,
  FileBarChart,
  ArrowUpRight,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Activity,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const modules = [
  { title: "Ambiental", desc: "Programas, indicadores y cumplimiento ambiental.", icon: Leaf, href: "/ambiental", color: "ambiental" },
  { title: "Social", desc: "Responsabilidad social y programas comunitarios.", icon: HandHeart, href: "/social", color: "social" },
  { title: "SST", desc: "Seguridad y salud en el trabajo.", icon: HardHat, href: "/sst", color: "sst" },
  { title: "Legal", desc: "Actos administrativos y consulta jurídica.", icon: Scale, href: "/legal", color: "legal" },
  { title: "GDB", desc: "Información geográfica y cartografía.", icon: Map, href: "/gestor-gdb", color: "gdb" },
  { title: "Reporte ICA", desc: "Reportes consolidados ICA por área.", icon: FileBarChart, href: "/reporte-integrado", color: "ica" },
] as const;

const kpis = [
  { label: "Obligaciones activas", value: "248", trend: "+12%", icon: Activity, tone: "text-module-sst" },
  { label: "Cumplimiento global", value: "87%", trend: "+4.2%", icon: CheckCircle2, tone: "text-success" },
  { label: "Próximas a vencer", value: "14", trend: "7 días", icon: Clock, tone: "text-warning" },
  { label: "Alertas críticas", value: "3", trend: "Atender hoy", icon: AlertTriangle, tone: "text-destructive" },
];

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const name = user?.email?.split("@")[0] ?? "usuario";

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto space-y-8">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 sm:p-8">
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground capitalize">Hola, {name} 👋</p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-balance">
              Centro operativo Viabiliza
            </h1>
            <p className="mt-1 text-sm text-muted-foreground max-w-xl">
              Monitorea cumplimiento ambiental, SST, social, legal y geoespacial en tiempo real.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/reporte-integrado")}>
              Ver reporte ICA
            </Button>
            <Button size="sm" className="bg-brand hover:bg-brand-dark text-brand-foreground" onClick={() => navigate("/gestor-ambiental")}>
              Nueva obligación
              <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className="p-4 sm:p-5 shadow-card hover:shadow-elegant transition-shadow border-border">
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground font-medium">{k.label}</p>
                  <p className="text-2xl sm:text-3xl font-bold tracking-tight">{k.value}</p>
                </div>
                <div className={`h-9 w-9 rounded-lg bg-muted flex items-center justify-center ${k.tone}`}>
                  <Icon className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                <TrendingUp className="h-3 w-3" />
                <span>{k.trend}</span>
              </div>
            </Card>
          );
        })}
      </section>

      {/* Modules */}
      <section className="space-y-3">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Módulos</h2>
            <p className="text-sm text-muted-foreground">Accesos rápidos a cada área de gestión.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {modules.map((m) => {
            const Icon = m.icon;
            return (
              <button
                key={m.title}
                onClick={() => navigate(m.href)}
                className="group text-left rounded-xl border border-border bg-card p-5 hover:border-foreground/20 hover:shadow-elegant transition-all hover:-translate-y-0.5"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center"
                    style={{
                      backgroundColor: `hsl(var(--module-${m.color}-soft))`,
                      color: `hsl(var(--module-${m.color}))`,
                    }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold tracking-tight">{m.title}</h3>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{m.desc}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Two-column: activity + tasks */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 border-border shadow-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold tracking-tight">Actividad reciente</h3>
              <p className="text-xs text-muted-foreground">Últimas acciones registradas en el sistema.</p>
            </div>
          </div>
          <ul className="divide-y divide-border">
            {[
              { mod: "Ambiental", color: "ambiental", txt: "Evidencia cargada en programa PM-04", t: "hace 5 min" },
              { mod: "SST", color: "sst", txt: "Inspección de seguridad completada", t: "hace 1 h" },
              { mod: "Legal", color: "legal", txt: "Nuevo acto administrativo registrado", t: "hace 3 h" },
              { mod: "GDB", color: "gdb", txt: "Capa cartográfica actualizada", t: "ayer" },
            ].map((it, i) => (
              <li key={i} className="flex items-center gap-3 py-3 text-sm">
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: `hsl(var(--module-${it.color}))` }}
                />
                <span className="font-medium text-muted-foreground w-20 shrink-0 text-xs uppercase tracking-wide">
                  {it.mod}
                </span>
                <span className="flex-1 truncate">{it.txt}</span>
                <span className="text-xs text-muted-foreground shrink-0">{it.t}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="p-5 border-border shadow-card">
          <h3 className="font-semibold tracking-tight">Tareas pendientes</h3>
          <p className="text-xs text-muted-foreground mb-4">Próximas a vencer.</p>
          <ul className="space-y-3">
            {[
              { txt: "Cargar evidencia trimestral aire", d: "2 días" },
              { txt: "Revisión jurídica resolución 0123", d: "4 días" },
              { txt: "Reporte ICA Q4", d: "7 días" },
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors">
                <div className="h-4 w-4 mt-0.5 rounded border border-muted-foreground/30" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{t.txt}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Vence en {t.d}</p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </section>
    </div>
  );
};

export default Home;
